export type UserRole = 'admin' | 'hr' | 'doctor' | 'nurse' | 'receptionist' | 'pharmacist' | 'lab_tech' | 'pathologist' | 'radiographer' | 'radiologist';

export function normalizeRole(role: string | null | undefined): UserRole {
  if (!role) return 'receptionist';
  
  const normalized = role.toLowerCase().trim();
  
  const roleMap: Record<string, UserRole> = {
    'tenant_admin': 'admin',
    'owner': 'admin',
    'administrator': 'admin',
    'reception': 'receptionist',
    'frontdesk': 'receptionist',
    'front_desk': 'receptionist',
    'staff': 'receptionist',
    'lab': 'lab_tech',
    'laboratory': 'lab_tech',
    'radiology': 'radiographer',
  };
  
  if (roleMap[normalized]) {
    return roleMap[normalized];
  }
  
  const validRoles: UserRole[] = ['admin', 'hr', 'doctor', 'nurse', 'receptionist', 'pharmacist', 'lab_tech', 'pathologist', 'radiographer', 'radiologist'];
  if (validRoles.includes(normalized as UserRole)) {
    return normalized as UserRole;
  }
  
  return 'receptionist';
}

export interface NavigationItem {
  name: string;
  href: string;
  permission: Permission;
  children?: { name: string; href: string }[];
}

export type Permission =
  | 'view_dashboard' | 'view_patients' | 'view_appointments' | 'view_doctors' 
  | 'view_staff' | 'view_consultations' | 'view_orders' | 'view_referrals' 
  | 'view_billing' | 'view_reports' | 'view_pharmacy' | 'view_settings'
  | 'view_insurance' | 'view_hr' | 'view_inventory' | 'view_payroll' | 'view_lab' | 'view_radiology'
  | 'view_ipd' | 'view_admissions' | 'view_beds' | 'view_discharge' | 'view_opd'
  | 'view_accounts' | 'view_payment_history' | 'view_receipts' | 'view_diagnosis'
  | 'manage_patients' | 'manage_appointments' | 'manage_consultations'
  | 'manage_pharmacy' | 'manage_billing' | 'manage_staff' | 'manage_settings'
  | 'manage_insurance' | 'manage_hr' | 'manage_inventory' | 'manage_payroll'
  | 'manage_accounts' | 'manage_prescriptions' | 'process_order_billing' | 'manage_diagnosis'
  | 'manage_invoices' | 'manage_payments' | 'manage_receipts' | 'manage_discounts' | 'manage_refunds'
  | 'enter_lab_results' | 'print_lab_reports' | 'manage_order_status' | 'verify_lab_results'
  | 'finalize_lab_reports' | 'create_lab_orders' | 'manage_lab_catalog'
  | 'create_radiology_orders' | 'perform_radiology_studies' | 'create_radiology_reports'
  | 'finalize_radiology_reports' | 'manage_radiology_catalog'
  | 'create_admissions' | 'manage_beds' | 'create_discharge_summary' | 'manage_insurance_claims'
  | 'view_patients_full' | 'view_patients_limited' | 'view_own_data_only'
  | 'manage_own_prescriptions' | 'view_own_reports' | 'create_appointments'
  | 'manage_users' | 'manage_software_billing';

export const rolePermissions: Record<UserRole, Permission[]> = {
  admin: [
    'view_dashboard', 'view_patients', 'view_appointments', 'view_doctors',
    'view_staff', 'view_consultations', 'view_orders', 'view_referrals',
    'view_billing', 'view_reports', 'view_pharmacy', 'view_settings', 'view_lab', 'view_radiology',
    'view_insurance', 'view_hr', 'view_inventory', 'view_payroll', 'view_ipd', 'view_opd',
    'view_accounts', 'view_payment_history', 'view_receipts', 'view_diagnosis',
    'manage_patients', 'manage_appointments', 'manage_consultations',
    'manage_pharmacy', 'manage_billing', 'manage_staff', 'manage_settings',
    'manage_insurance', 'manage_hr', 'manage_inventory', 'manage_payroll', 
    'manage_accounts', 'process_order_billing', 'enter_lab_results', 
    'print_lab_reports', 'manage_order_status', 'manage_users', 'manage_diagnosis',
    'manage_invoices', 'manage_payments', 'manage_receipts', 'manage_discounts', 'manage_refunds',
    'verify_lab_results', 'finalize_lab_reports', 'create_lab_orders', 'manage_lab_catalog',
    'create_radiology_orders', 'perform_radiology_studies', 'create_radiology_reports',
    'finalize_radiology_reports', 'manage_radiology_catalog',
    'create_admissions', 'manage_beds', 'create_discharge_summary', 'manage_insurance_claims',
    'manage_software_billing'
  ],
  hr: [
    'view_dashboard', 'view_patients', 'view_appointments', 'view_doctors',
    'view_hr', 'view_payroll', 'view_reports', 'view_accounts', 'view_payment_history',
    'manage_staff', 'manage_hr', 'manage_payroll', 'manage_accounts'
  ],
  doctor: [
    'view_dashboard', 'view_patients_limited', 'view_appointments', 'view_consultations',
    'view_orders', 'view_referrals', 'view_own_reports', 'view_settings', 'view_lab', 'view_radiology',
    'view_opd', 'view_diagnosis', 'manage_consultations', 'manage_own_prescriptions', 'view_own_data_only',
    'print_lab_reports', 'view_ipd', 'view_admissions', 'create_admissions', 'create_discharge_summary'
  ],
  nurse: [
    'view_dashboard', 'view_patients', 'view_appointments', 'view_consultations',
    'view_orders', 'view_lab', 'manage_patients', 'manage_order_status',
    'view_ipd', 'view_admissions', 'manage_beds'
  ],
  receptionist: [
    'view_dashboard', 'view_patients', 'view_appointments', 'view_billing', 'view_lab', 'view_radiology',
    'view_insurance', 'view_opd', 'view_settings', 'view_accounts', 'view_payment_history', 'view_receipts',
    'view_diagnosis', 'manage_patients', 'manage_appointments', 'manage_billing',
    'manage_insurance', 'process_order_billing', 'create_lab_orders', 'create_radiology_orders',
    'manage_payments', 'manage_receipts',
    'view_ipd', 'create_admissions', 'manage_beds', 'manage_insurance_claims'
  ],
  pharmacist: [
    'view_dashboard', 'view_pharmacy', 'view_consultations', 'view_orders',
    'manage_pharmacy'
  ],
  lab_tech: [
    'view_dashboard', 'view_lab', 'view_orders', 'enter_lab_results', 'manage_order_status',
    'print_lab_reports'
  ],
  pathologist: [
    'view_dashboard', 'view_lab', 'view_orders', 'view_diagnosis', 'enter_lab_results', 'verify_lab_results',
    'finalize_lab_reports', 'print_lab_reports', 'manage_order_status', 'manage_diagnosis'
  ],
  radiographer: [
    'view_dashboard', 'view_radiology', 'view_orders', 'perform_radiology_studies',
    'manage_order_status'
  ],
  radiologist: [
    'view_dashboard', 'view_radiology', 'view_orders', 'view_diagnosis', 'create_radiology_reports',
    'finalize_radiology_reports', 'manage_order_status'
  ]
};

export function can(role: UserRole, permission: Permission): boolean {
  return rolePermissions[role]?.includes(permission) ?? false;
}

export function getNavigationItems(role: UserRole): NavigationItem[] {
  const allItems: NavigationItem[] = [
    { name: 'Dashboard', href: '/', permission: 'view_dashboard' as Permission },
    { 
      name: 'Patient Info', 
      href: '/patients', 
      permission: 'view_patients' as Permission,
      children: [
        { name: 'Patient', href: '/patients' },
        { name: 'Patient Report Check', href: '/patients/report-check' },
        { name: 'Case Picture Manages', href: '/patients/case-picture' },
        { name: 'Report Picture Upload', href: '/patients/report-picture' },
        { name: 'Bulk SMS Send', href: '/patients/bulk-sms' },
        { name: 'SMS Others Number', href: '/patients/sms-others' },
        { name: 'Bulk SMS History', href: '/patients/sms-history' },
      ]
    },
    { 
      name: 'Appointments', 
      href: '/appointments', 
      permission: 'view_appointments' as Permission,
      children: [
        { name: 'Appointments Manages', href: '/appointments' },
        { name: 'Appointment Report', href: '/appointments/report' },
      ]
    },
    { 
      name: 'OPD', 
      href: '/opd', 
      permission: 'view_opd' as Permission,
      children: [
        { name: 'Service Catalog', href: '/opd/services' },
        { name: 'Referral Contracts', href: '/opd/referrals' },
        { name: 'Referral Payouts', href: '/opd/payouts' },
      ]
    },
    { 
      name: 'Doctors', 
      href: '/doctors', 
      permission: 'view_doctors' as Permission,
      children: [
        { name: 'Manage', href: '/doctors/manage' },
        { name: 'Departments', href: '/doctors/departments' },
        { name: 'Blood Groups', href: '/doctors/blood-groups' },
        { name: 'P.C Doctors', href: '/doctors/pc-doctors' },
        { name: 'Report Doctors', href: '/doctors/report-doctors' },
        { name: 'Schedules', href: '/doctors/schedules' },
        { name: 'Breaks', href: '/doctors/breaks' },
        { name: 'Holidays', href: '/doctors/holidays' },
      ]
    },
    { name: 'Staff', href: '/staff', permission: 'view_staff' as Permission },
    { name: 'Consultations', href: '/consultations', permission: 'view_consultations' as Permission },
    { name: 'Orders', href: '/orders', permission: 'view_orders' as Permission },
    { 
      name: 'Lab', 
      href: '/lab', 
      permission: 'view_lab' as Permission,
      children: [
        { name: 'Worklist', href: '/lab' },
        { name: 'Test Catalog', href: '/lab/catalog' },
        { name: 'New Order', href: '/lab/orders/new' },
        { name: 'Analyzer Import', href: '/lab/analyzer-import' },
        { name: 'TAT Dashboard', href: '/lab/tat' },
        { name: 'Reagents', href: '/lab/reagents' },
        { name: 'Notification Log', href: '/lab/notifications' },
      ]
    },
    { 
      name: 'Radiology', 
      href: '/radiology', 
      permission: 'view_radiology' as Permission,
      children: [
        { name: 'Worklist', href: '/radiology' },
        { name: 'New Order', href: '/radiology/new' },
        { name: 'Test Catalog', href: '/radiology/catalog' },
        { name: 'TAT Dashboard', href: '/radiology/tat' },
      ]
    },
    { 
      name: 'Diagnosis', 
      href: '/diagnosis', 
      permission: 'view_diagnosis' as Permission,
      children: [
        { name: 'Diagnosis Categories', href: '/diagnosis/categories' },
        { name: 'Test Name', href: '/diagnosis/test-names' },
        { name: 'Pathology Setup', href: '/diagnosis/pathology-setup' },
        { name: 'Pathology Lab Report', href: '/diagnosis/pathology-reports' },
        { name: 'Molecular Path Setup', href: '/diagnosis/molecular-setup' },
        { name: 'Molecular Path Report', href: '/diagnosis/molecular-reports' },
        { name: 'USG Setup', href: '/diagnosis/usg-setup' },
        { name: 'USG Report', href: '/diagnosis/usg-reports' },
        { name: 'X-Ray Report', href: '/diagnosis/xray-reports' },
      ]
    },
    { 
      name: 'IPD', 
      href: '/ipd', 
      permission: 'view_ipd' as Permission,
      children: [
        { name: 'Admissions', href: '/ipd' },
        { name: 'Bed Management', href: '/ipd/beds' },
        { name: 'Discharge Summaries', href: '/ipd/discharge' },
        { name: 'Discharged Patients', href: '/ipd/discharged' },
        { name: 'Discharged Billing', href: '/ipd/discharged-billing' },
      ]
    },
    { name: 'Referrals', href: '/referrals', permission: 'view_referrals' as Permission },
    { name: 'Prescriptions', href: '/doctor/prescriptions', permission: 'manage_own_prescriptions' as Permission },
    {
      name: 'Billings/Reception', 
      href: '/billings', 
      permission: 'view_billing' as Permission,
      children: [
        { name: 'Account', href: '/billings/account' },
        { name: 'Transaction Head', href: '/billings/transaction-head' },
        { name: 'Set Test Price', href: '/billings/set-test-price' },
        { name: 'Invoices', href: '/billings/invoices' },
        { name: 'Due Collection', href: '/billings/due-collection' },
        { name: 'Due Report', href: '/billings/due-report' },
        { name: 'Invoice Report', href: '/billings/invoice-report' },
        { name: 'P.C Invoice Report', href: '/billings/pc-invoice-report' },
        { name: 'P.C Invoice Date Group', href: '/billings/pc-invoice-date-group' },
        { name: 'Cash Collection Report', href: '/billings/cash-collection-report' },
        { name: 'Due Collection Report', href: '/billings/due-collection-report' },
        { name: 'Doctor Wise Report', href: '/billings/doctor-wise-report' },
        { name: 'Counter Wise Report', href: '/billings/counter-wise-report' },
        { name: 'Test Report', href: '/billings/test-report' },
        { name: 'Voucher Head', href: '/billings/voucher-head' },
        { name: 'Debit Voucher', href: '/billings/debit-voucher' },
        { name: 'Debit V. Check', href: '/billings/debit-v-check' },
        { name: 'Credit Voucher', href: '/billings/credit-voucher' },
        { name: 'PC Payment', href: '/billings/pc-payment' },
        { name: 'Payment Reports', href: '/billings/payment-reports' },
        { name: 'General Ledger', href: '/billings/general-ledger' },
        { name: 'Daily Cashbook', href: '/billings/daily-cashbook' },
        { name: 'Summary Report', href: '/billings/summary-report' },
        { name: 'Expense Summary', href: '/billings/expense-summary' },
        { name: 'Commission Reports', href: '/billings/commission-reports' },
        { name: 'Balance Transfer', href: '/billings/balance-transfer' },
        { name: 'Fund Manages', href: '/billings/fund-manages' },
      ]
    },
    { name: 'Reports', href: '/reports', permission: 'view_reports' as Permission },
    { 
      name: 'Pharmacy', 
      href: '/pharmacy', 
      permission: 'view_pharmacy' as Permission,
      children: [
        { name: 'Dashboard', href: '/pharmacy' },
        { name: 'Sales', href: '/pharmacy/sales' },
        { name: 'Add New Sale', href: '/pharmacy/pos' },
        { name: 'Expense', href: '/pharmacy/expenses' },
        { name: 'Add Expense', href: '/pharmacy/expenses/add' },
        { name: 'Expense Categories', href: '/pharmacy/expense-categories' },
        { name: 'Pharmacy Report', href: '/pharmacy/reports' },
        { name: 'Product Manages', href: '/pharmacy/medicines' },
        { name: 'Supplier Manages', href: '/pharmacy/suppliers' },
        { name: 'Unit Manages', href: '/pharmacy/units' },
      ]
    },
    { name: 'Insurance', href: '/insurance', permission: 'view_insurance' as Permission },
    { name: 'HR', href: '/hr', permission: 'view_hr' as Permission },
    { name: 'Inventory', href: '/inventory', permission: 'view_inventory' as Permission },
    { name: 'Payroll', href: '/payroll', permission: 'manage_payroll' as Permission },
    { name: 'Users', href: '/settings/users', permission: 'manage_users' as Permission },
    { 
      name: 'Software Billing', 
      href: '/softwarebilling', 
      permission: 'manage_software_billing' as Permission,
      children: [
        { name: 'Subscription', href: '/softwarebilling/subscription' },
        { name: 'Payment Now', href: '/softwarebilling/paymentnow' },
        { name: 'SMS Buy', href: '/softwarebilling/smsbuy' },
        { name: 'SMS History', href: '/softwarebilling/smshistory' },
      ]
    },
    { 
      name: 'Settings', 
      href: '/settings', 
      permission: 'view_settings' as Permission,
      children: [
        { name: 'System Settings', href: '/settings?tab=system' },
        { name: 'Users', href: '/settings/users-management' },
        { name: 'User Roles', href: '/settings/user-roles' },
        { name: 'Notification Preferences', href: '/settings/notifications' },
      ]
    },
  ];
  
  return allItems.filter(item => can(role, item.permission));
}

export function isDoctor(user: any): boolean {
  console.warn('isDoctor is deprecated. Use database-backed role checks instead.');
  const role = user?.user_metadata?.role || user?.app_metadata?.role;
  return role === 'doctor';
}

export function getDoctorIdFromUser(user: any): string | null {
  const email = user?.email;
  if (!email) return null;
  
  const doctorEmailMap: Record<string, string> = {
    'doctor@medsystem.com': 'doctor-1', // Generic doctor login
    'emily.johnson@medsystem.com': 'doctor-1',
    'michael.chen@medsystem.com': 'doctor-2', 
    'sarah.williams@medsystem.com': 'doctor-3'
  };
  
  return doctorEmailMap[email] || null;
}

export function myScopeFilter(userId: string | null) {
  return userId ? { doctorId: userId } : {};
}

export function canViewPatientFull(user: any, patientId: string): boolean {
  if (!isDoctor(user)) return can(getUserRole(user), 'manage_patients');
  
  const doctorId = getDoctorIdFromUser(user);
  if (!doctorId) return false;
  
  return hasActiveEncounterWithPatient(doctorId, patientId);
}

export function canEditPrescription(user: any, prescriptionId: string): boolean {
  if (!isDoctor(user)) return can(getUserRole(user), 'manage_prescriptions');
  
  const doctorId = getDoctorIdFromUser(user);
  if (!doctorId) return false;
  
  return isPrescriptionOwnedByDoctor(prescriptionId, doctorId);
}

export function canViewOwnDataOnly(user: any): boolean {
  return isDoctor(user) && can(getUserRole(user), 'view_own_data_only');
}

export function getUserRole(user: any): UserRole {
  console.warn('getUserRole is deprecated. Use getUserRoleFromDB instead.');
  return user?.user_metadata?.role || user?.app_metadata?.role || 'receptionist';
}

export async function getUserRoleFromDB(userId: string): Promise<UserRole | null> {
  const { supabase } = await import('./supabase');
  
  const { data, error } = await supabase
    .from('user_roles')
    .select('*, roles(name)')
    .eq('user_id', userId)
    .single();
  
  if (error || !data) return null;
  
  const roleName = (data as any).roles?.name;
  return normalizeRole(roleName);
}

export async function hasRoleDB(userId: string, roleName: string): Promise<boolean> {
  const { supabase } = await import('./supabase');
  
  const { data, error } = await supabase.rpc('has_role', {
    user_id: userId,
    role_name: roleName
  });
  
  if (error) {
    console.error('Error checking role:', error);
    return false;
  }
  
  return data === true;
}

export async function isAdminDB(userId: string): Promise<boolean> {
  const { supabase } = await import('./supabase');
  
  const { data, error } = await supabase.rpc('is_admin', {
    p_user_id: userId
  });
  
  if (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
  
  return data === true;
}

export async function canManageStaffDB(userId: string): Promise<boolean> {
  const isAdmin = await isAdminDB(userId);
  if (isAdmin) return true;
  
  const isHR = await hasRoleDB(userId, 'hr');
  return isHR;
}

export function scopeToMyData(user: any, baseQuery: any = {}) {
  if (!canViewOwnDataOnly(user)) return baseQuery;
  
  const doctorId = getDoctorIdFromUser(user);
  if (!doctorId) return { ...baseQuery, doctorId: 'none' };
  
  return { ...baseQuery, doctorId };
}

export function auditLog(action: string, resourceType: string, resourceId: string, userId: string) {
  const logEntry = {
    timestamp: new Date().toISOString(),
    action,
    resourceType,
    resourceId,
    byUserId: userId,
  };
  
  console.log('AUDIT:', logEntry);
  
  if (typeof window !== 'undefined') {
    const auditLogs = JSON.parse(localStorage.getItem('auditLogs') || '[]');
    auditLogs.push(logEntry);
    localStorage.setItem('auditLogs', JSON.stringify(auditLogs.slice(-1000)));
  }
}

function hasActiveEncounterWithPatient(doctorId: string, patientId: string): boolean {
  if (typeof window !== 'undefined') {
    const encounters = JSON.parse(localStorage.getItem('mockEncounters') || '[]');
    return encounters.some((encounter: any) => 
      encounter.doctorId === doctorId && encounter.patientId === patientId
    );
  }
  return false;
}

function isPrescriptionOwnedByDoctor(prescriptionId: string, doctorId: string): boolean {
  if (typeof window !== 'undefined') {
    const prescriptions = JSON.parse(localStorage.getItem('mockPrescriptions') || '[]');
    const prescription = prescriptions.find((p: any) => p.id === prescriptionId);
    if (prescription) {
      const encounters = JSON.parse(localStorage.getItem('mockEncounters') || '[]');
      const encounter = encounters.find((e: any) => e.id === prescription.encounterId);
      return encounter?.doctorId === doctorId;
    }
  }
  return false;
}
